import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, Chip } from '@mui/material';
import { Add } from '@mui/icons-material';
import { complaintsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext'; // adjust path as needed
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';

const safeArray = (val) => Array.isArray(val) ? val : [];

export default function Complaints() {
  const { t } = useTranslation();
  const { user } = useAuth();
  // Extract group names - handle both old format (strings) and new format (objects)
  const userGroups = user?.groups || [];
  const groupNames = userGroups.map(g => typeof g === 'string' ? g : g.name);
  const isAdmin = user && (groupNames.some(g => ['admin', 'maintenance_staff', 'building_admin'].includes(g.toLowerCase())) ||
                          ['super_admin', 'building_admin', 'maintenance_staff'].includes(user?.role));
 
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    userId: user?.userId || '',
    description: '',
    category: '',
    priority: 'MEDIUM',
    flatId: '',
    status: 'OPEN', // <-- set to OPEN
  });
  const [error, setError] = useState('');
  const queryClient = useQueryClient();
  const { data: complaints = [], isLoading } = useQuery('complaints', async () => (await complaintsAPI.getAll()).data);

  const createMutation = useMutation(complaintsAPI.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('complaints');
      handleClose();
    },
    onError: (error) => {
      setError(error.response?.data?.message || 'Failed to create complaint');
    },
  });

  const updateStatusMutation = useMutation(
    ({ complaintId, status }) => complaintsAPI.update(complaintId, { status }),
    {
      onSuccess: () => queryClient.invalidateQueries('complaints'),
      onError: (error) => setError(error.response?.data?.message || 'Failed to update status'),
    }
  );

  const handleOpen = () => {
    setFormData({ title: '', description: '', category: '', priority: 'MEDIUM', flatId: '', status: 'OPEN' }); // <-- set to OPEN
    setError('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({ title: '', description: '', category: '', priority: 'MEDIUM', flatId: '', status: 'OPEN' }); // <-- set to OPEN
    setError('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  if (complaints && complaints.message) {
    return <Typography color="error">{complaints.message}</Typography>;
  }
  const complaintsSafe = safeArray(complaints);

  const getStatusColor = (status) => {
    switch (status) {
      case 'OPEN': return 'error';
      case 'IN_PROGRESS': return 'warning';
      case 'RESOLVED': return 'success';
      case 'CLOSED': return 'default';
      default: return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'URGENT': return 'error';
      case 'HIGH': return 'warning';
      case 'MEDIUM': return 'info';
      case 'LOW': return 'success';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        {/*<Typography variant="h4">{t('complaints.title')}</Typography> */}
        <Button variant="contained" startIcon={<Add />} onClick={handleOpen}>
          {t('complaints.addComplaint')}
        </Button>
      </Box>
      {isLoading ? (
        <Typography>{t('common.loading')}</Typography>
      ) : complaintsSafe.length === 0 ? (
        <Typography>{t('complaints.noComplaints')}</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('complaints.title')}</TableCell>
                <TableCell>{t('complaints.description')}</TableCell>
                <TableCell>{t('complaints.category')}</TableCell>
                <TableCell>{t('complaints.priority')}</TableCell>
                <TableCell>{t('complaints.status')}</TableCell>
                <TableCell>{t('complaints.flatId')}</TableCell>
                {isAdmin && <TableCell>{t('complaints.updateStatus')}</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {complaintsSafe.map((complaint) => (
                <TableRow key={complaint.complaintId}>
                  <TableCell>{complaint.title}</TableCell>
                  <TableCell>{complaint.description}</TableCell>
                  <TableCell>{complaint.category}</TableCell>
                  <TableCell>
                    <Chip 
                      label={complaint.priority} 
                      color={getPriorityColor(complaint.priority)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={complaint.status} 
                      color={getStatusColor(complaint.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{complaint.flatId}</TableCell>
                  {isAdmin && (
                    <TableCell>
                      <TextField
                        label="Update Status"
                        value={complaint.status}
                        onChange={e => updateStatusMutation.mutate({ complaintId: complaint.complaintId, status: e.target.value })}
                        select
                        size="small"
                        SelectProps={{ native: true }}
                      >
                        <option value="OPEN">{t('complaints.statuses.open')}</option>
                        <option value="IN_PROGRESS">{t('complaints.statuses.inProgress')}</option>
                        <option value="RESOLVED">{t('complaints.statuses.resolved')}</option>
                        <option value="CLOSED">{t('complaints.statuses.closed')}</option>
                      </TextField>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="md" 
        fullWidth
        fullScreen={window.innerWidth < 600} // Full screen on mobile
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '90%', md: '600px' },
            maxHeight: { xs: '100vh', sm: '90vh' },
            margin: { xs: 0, sm: 'auto' },
            borderRadius: { xs: 0, sm: 1 }
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            pb: 1,
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            borderBottom: '1px solid',
            borderColor: 'divider',
            position: 'sticky',
            top: 0,
            backgroundColor: 'background.paper',
            zIndex: 1
          }}
        >
          {t('complaints.addNewComplaint')}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent
            sx={{ 
              p: { xs: 1, sm: 2 },
              '&.MuiDialogContent-root': {
                paddingTop: { xs: 1, sm: 2 }
              }
            }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: { xs: 1, sm: 2 } }}>
                {error}
              </Alert>
            )}
            <Box sx={{ 
              display: 'grid', 
              gap: { xs: 1, sm: 2 },
              gridTemplateColumns: '1fr'
            }}>
              <TextField 
                label={t('complaints.title')} 
                value={formData.title} 
                onChange={e => setFormData({ ...formData, title: e.target.value })} 
                required 
                fullWidth 
                margin="dense"
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: { xs: '14px', sm: '16px' }
                  }
                }}
              />
              <TextField 
                label={t('complaints.description')} 
                value={formData.description} 
                onChange={e => setFormData({ ...formData, description: e.target.value })} 
                required 
                fullWidth 
                multiline 
                rows={3} 
                margin="dense"
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: { xs: '14px', sm: '16px' }
                  }
                }}
              />
              <TextField
                label={t('complaints.category')}
                value={formData.category}
                onChange={e => setFormData({ ...formData, category: e.target.value })}
                required
                fullWidth
                select
                margin="dense"
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: { xs: '14px', sm: '16px' }
                  }
                }}
                SelectProps={{ native: true }}
              >
                <option value="">{t('complaints.selectCategory')}</option>
                <option value="PLUMBING">{t('complaints.categories.plumbing')}</option>
                <option value="ELECTRICAL">{t('complaints.categories.electrical')}</option>
                <option value="CLEANING">{t('complaints.categories.cleaning')}</option>
                <option value="SECURITY">{t('complaints.categories.security')}</option>
                <option value="MAINTENANCE">{t('complaints.categories.maintenance')}</option>
                <option value="OTHER">{t('complaints.categories.other')}</option>
              </TextField>
              <TextField 
                label={t('complaints.priority')} 
                value={formData.priority} 
                onChange={e => setFormData({ ...formData, priority: e.target.value })} 
                required 
                fullWidth 
                select 
                margin="dense"
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: { xs: '14px', sm: '16px' }
                  }
                }}
                SelectProps={{ native: true }}
              >
                <option value="URGENT">{t('complaints.priorities.urgent')}</option>
                <option value="HIGH">{t('complaints.priorities.high')}</option>
                <option value="MEDIUM">{t('complaints.priorities.medium')}</option>
                <option value="LOW">{t('complaints.priorities.low')}</option>
              </TextField>
              <TextField 
                label={t('complaints.flatId')} 
                value={formData.flatId} 
                onChange={e => setFormData({ ...formData, flatId: e.target.value })} 
                required 
                fullWidth 
                margin="dense"
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: { xs: '14px', sm: '16px' }
                  }
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{ 
              p: { xs: 1, sm: 2 },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 1, sm: 0 },
              position: 'sticky',
              bottom: 0,
              backgroundColor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Button 
              onClick={handleClose}
              fullWidth={window.innerWidth < 600}
              sx={{ fontSize: { xs: '14px', sm: '16px' } }}
            >
              {t('common.cancel')}
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={createMutation.isLoading}
              fullWidth={window.innerWidth < 600}
              sx={{ 
                ml: { xs: 0, sm: 1 },
                fontSize: { xs: '14px', sm: '16px' }
              }}
            >
              {createMutation.isLoading ? t('complaints.adding') : t('common.add')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};